/**
 * Currency Switcher
 * Uses Shopify's native market pricing — no client-side conversion.
 * Switches currency by submitting Shopify's localization form with the correct country_code.
 */

class CurrencySwitcher {
  constructor() {
    // Currency → representative country code for Shopify localization
    this.currencyCountryMap = {
      'SEK': 'SE',
      'EUR': 'DE',
      'USD': 'US'
    };
  }

  init() {
    // Get current currency from Shopify's localization data
    this.currentCurrency = window.theme?.market?.currency
      || window.theme?.localization?.currency
      || 'SEK';

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.setupCurrencySelector();
  }

  /**
   * Switch currency by submitting Shopify's native localization form.
   * Always reuses the Shopify-generated form (has correct action + CSRF token).
   */
  switchCurrency(currency) {
    if (currency === this.currentCurrency) return;

    const countryCode = this.currencyCountryMap[currency];
    if (!countryCode) return;

    // Find the Shopify-generated localization form (footer or header)
    const form = document.querySelector('form[id*="localization"]');

    if (form) {
      // Ensure country_code input exists
      let countryInput = form.querySelector('input[name="country_code"]');
      if (!countryInput) {
        countryInput = document.createElement('input');
        countryInput.type = 'hidden';
        countryInput.name = 'country_code';
        form.appendChild(countryInput);
      }
      countryInput.value = countryCode;
      form.submit();
    } else {
      // Fallback: use fetch with the Shopify localization endpoint
      const formData = new FormData();
      formData.append('country_code', countryCode);
      formData.append('return_to', window.location.pathname + window.location.search);

      fetch('/localization', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      }).then(() => {
        window.location.reload();
      }).catch(() => {
        // Last resort: redirect with country parameter
        const url = new URL(window.location.href);
        url.searchParams.set('country', countryCode);
        window.location.href = url.toString();
      });
    }
  }

  /**
   * Build the currency selector UI in the header
   */
  setupCurrencySelector() {
    let container = document.getElementById('honn-currency-selector-container');

    if (!container) {
      container = document.createElement('div');
      container.id = 'honn-currency-selector-container';
      container.className = 'honn-currency-selector-container disclosure--root';
      container.setAttribute('data-type', 'currency');

      // disclosure--main
      const main = document.createElement('div');
      main.className = 'disclosure--main';

      // toggle button
      const toggle = document.createElement('button');
      toggle.className = 'disclosure--toggle';
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-controls', 'honn-currency-form');
      toggle.setAttribute('data-item', 'nav-text');

      // current option display
      const currentOption = document.createElement('div');
      currentOption.className = 'disclosure--current-option';
      currentOption.id = 'honn-currency-current';
      currentOption.textContent = this.currentCurrency;

      // chevron icons
      const chevronUp = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      chevronUp.setAttribute('class', 'icon--root icon--chevron-up');
      chevronUp.setAttribute('width', '10');
      chevronUp.setAttribute('height', '6');
      chevronUp.setAttribute('viewBox', '0 0 10 6');
      chevronUp.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      chevronUp.innerHTML = '<path d="m5 1.942-3.53 3.53-.707-.707 3.53-3.53L5 .528l4.237 4.237-.707.707L5 1.942Z" fill="#000" fill-rule="nonzero"/>';

      const chevronDown = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      chevronDown.setAttribute('class', 'icon--root icon--chevron-down');
      chevronDown.setAttribute('width', '10');
      chevronDown.setAttribute('height', '6');
      chevronDown.setAttribute('viewBox', '0 0 10 6');
      chevronDown.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      chevronDown.innerHTML = '<path d="M5 4.058 8.53.528l.707.707-3.53 3.53L5 5.472.763 1.235 1.47.528 5 4.058Z" fill="#000" fill-rule="nonzero"/>';

      toggle.appendChild(currentOption);
      toggle.appendChild(chevronUp);
      toggle.appendChild(chevronDown);

      // dropdown menu
      const form = document.createElement('ul');
      form.id = 'honn-currency-form';
      form.className = 'disclosure--form';
      form.setAttribute('data-transition-item', 'viewport');
      form.setAttribute('data-transition-type', 'scale-in');
      form.setAttribute('data-transition-trigger', 'custom');

      const currencies = [
        { value: 'SEK', label: 'SEK' },
        { value: 'EUR', label: 'EUR' },
        { value: 'USD', label: 'USD' }
      ];

      currencies.forEach(currency => {
        const option = document.createElement('li');
        option.className = 'disclosure--option';
        option.setAttribute('tabindex', '0');
        option.setAttribute('data-item', 'nav-text--sub');
        option.setAttribute('data-value', currency.value);
        option.setAttribute('aria-current', currency.value === this.currentCurrency ? 'true' : 'false');
        option.textContent = currency.label;
        form.appendChild(option);
      });

      // hidden input
      const hiddenInput = document.createElement('input');
      hiddenInput.type = 'hidden';
      hiddenInput.className = 'disclosure--input';
      hiddenInput.value = this.currentCurrency;
      hiddenInput.setAttribute('data-item', 'disclosure');

      // assemble
      main.appendChild(toggle);
      main.appendChild(form);
      main.appendChild(hiddenInput);
      container.appendChild(main);

      // insert into header
      const headerRightSide = document.querySelector('.header--right-side');
      if (headerRightSide) {
        const languageSwitcher = headerRightSide.querySelector('.header--language-switcher');
        if (languageSwitcher) {
          languageSwitcher.parentNode.insertBefore(container, languageSwitcher.nextSibling);
        } else {
          const cartLink = headerRightSide.querySelector('.cart-link');
          if (cartLink) {
            cartLink.parentNode.insertBefore(container, cartLink.nextSibling);
          } else {
            headerRightSide.appendChild(container);
          }
        }
      } else {
        document.body.appendChild(container);
      }

      this.addCurrencySelectorStyles();
      this.setupEvents(container, toggle, form, currentOption, hiddenInput);
    }
  }

  /**
   * Event listeners for the currency selector
   */
  setupEvents(container, toggle, form, currentOption, hiddenInput) {
    // Toggle dropdown
    toggle.addEventListener('click', (e) => {
      e.preventDefault();
      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      this.toggleForm(toggle, form, !isExpanded);
    });

    // Click option → switch currency via Shopify localization
    const options = form.querySelectorAll('.disclosure--option');
    options.forEach(option => {
      option.addEventListener('click', (e) => {
        e.preventDefault();
        const currency = option.dataset.value;
        currentOption.textContent = currency;
        options.forEach(opt => opt.setAttribute('aria-current', opt === option ? 'true' : 'false'));
        hiddenInput.value = currency;
        this.toggleForm(toggle, form, false);
        this.switchCurrency(currency);
      });

      option.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          option.click();
        }
      });
    });

    // Close on outside click
    document.addEventListener('click', (e) => {
      if (!container.contains(e.target)) {
        this.toggleForm(toggle, form, false);
      }
    });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
        this.toggleForm(toggle, form, false);
        toggle.focus();
      }
    });
  }

  toggleForm(toggle, form, show) {
    toggle.setAttribute('aria-expanded', show);
    form.setAttribute('data-transition-active', show);
  }

  addCurrencySelectorStyles() {
    if (document.getElementById('honn-currency-selector-styles')) return;

    const style = document.createElement('style');
    style.id = 'honn-currency-selector-styles';
    style.textContent = `
      .header--right-side .honn-currency-selector-container {
        display: flex !important;
        align-items: center;
        margin-left: 10px;
        position: relative;
      }
      .header--right-side .honn-currency-selector-container.disclosure--root {
        flex-flow: row nowrap;
        align-items: center;
      }
      .header--right-side .honn-currency-selector-container .disclosure--main {
        width: auto;
        flex: 0 0 auto;
      }
      .header--right-side .honn-currency-selector-container .disclosure--toggle {
        margin: 0 4px 0 -4px;
        padding: calc(var(--spacing--1) / 2);
      }
      .header--right-side .honn-currency-selector-container .disclosure--current-option {
        font-size: 13px;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        font-weight: 500;
        width: 30px;
      }
      .header--right-side .honn-currency-selector-container .disclosure--form {
        width: 60px;
        min-width: 60px;
        max-width: 60px;
        left: 50%;
        transform: translateX(-58%);
        right: auto;
      }
      .header--right-side .honn-currency-selector-container .disclosure--option {
        font-size: 13px;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      }
      @media only screen and (max-width: 768px) {
        .header--right-side .honn-currency-selector-container {
          margin-left: 8px;
        }
      }
    `;
    document.head.appendChild(style);
  }
}

// Initialize
function initCurrencySwitcher() {
  if (!window.currencySwitcher) {
    window.currencySwitcher = new CurrencySwitcher();
  }
  window.currencySwitcher.init();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCurrencySwitcher);
} else {
  initCurrencySwitcher();
}
